import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Button, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem,
  TextField, Switch, FormControlLabel, CircularProgress, Tooltip, Collapse, InputAdornment,
} from '@mui/material';
import {
  Edit as EditIcon, Sync as SyncIcon, ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon, PersonAdd as PersonAddIcon, Search as SearchIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { AI_ENGINE_URL, BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson } from '../../utils/apiFout';
import CorveeMemberPanel from '../Corvee/CorveeMemberPanel';

const PERM_URL = `${AI_ENGINE_URL}/api/v1/permissions`;

const ROLE_COLORS = {
  admin: '#ef4444',
  management: '#8b5cf6',
  projectleider: '#3b82f6',
  team: '#6b7280',
};

const ROLE_LABELS = {
  admin: 'Admin',
  management: 'Management',
  projectleider: 'Projectleider',
  team: 'Teamlid',
};

// Groepeer features per categorie, in de volgorde uit de API (anders alfabetisch).
// Geeft een array van [categorieNaam, [ [key, feat], ... ] ] terug.
function groupFeaturesByCategory(features, order = []) {
  const groups = {};
  for (const [key, feat] of Object.entries(features || {})) {
    const cat = feat?.category || 'Overig';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push([key, feat]);
  }
  const ordered = [];
  const seen = new Set();
  for (const cat of order) {
    if (groups[cat]) { ordered.push([cat, groups[cat]]); seen.add(cat); }
  }
  for (const cat of Object.keys(groups).sort()) {
    if (!seen.has(cat)) ordered.push([cat, groups[cat]]);
  }
  return ordered;
}

export default function UserManagement({ userEmail }) {
  const [users, setUsers] = useState([]);
  const [roleConfig, setRoleConfig] = useState({ roles: {}, features: {}, defaults: {} });
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [grippData, setGrippData] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [q, setQ] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes] = await Promise.all([
        authFetch(`${PERM_URL}/users`),
        authFetch(`${PERM_URL}/roles`),
      ]);
      const usersData = await leesJson(usersRes, 'Gebruikers laden');
      const rolesData = await leesJson(rolesRes, 'Rollen laden');
      setUsers(usersData.users || []);
      setRoleConfig(rolesData);
    } catch (err) {
      toast.error(err.message || 'Fout bij laden gebruikers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      // Ververs de Personen-mirror + seed de app-gebruikers in één Node-call. Beide
      // engine-routes zijn internal-only (X-Internal-Key) → moeten via de backend, niet
      // rechtstreeks vanuit de browser (dat gaf de 'requires X-Internal-Key'-fout).
      const res = await authFetch(`${BACKEND_URL}/Api/personen/sync`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.detail || `Fout ${res.status}`);
      toast.success(`${data.processed ?? 0} gebruikers gesynchroniseerd vanuit Airtable`);
      await fetchData();
    } catch (err) {
      toast.error('Sync mislukt: ' + err.message);
    } finally {
      setSeeding(false);
    }
  };

  const handleSyncGripp = async () => {
    setSyncing(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/gripp/employees`);
      const data = await leesJson(res, 'Gripp-medewerkers laden');
      const employees = data.employees || [];
      setGrippData(employees);

      // Update users that have a gripp_employee_id or match by email
      let updated = 0;
      for (const user of users) {
        const grippMatch = employees.find(e => {
          if (user.gripp_employee_id && e.id === user.gripp_employee_id) return true;
          const grippEmail = e.emailaddresses?.[0]?.emailaddress || e.email || '';
          return grippEmail.toLowerCase() === user.email.toLowerCase();
        });
        if (grippMatch) {
          // Alleen Gripp-/contractvelden: rol en rechten blijven op de server staan zoals ze zijn
          // (eerst reset deze knop ze, daarna kon een verouderde browserstand ze terugzetten).
          // Contracturen geeft Gripp hier niet (staan in de arbeidsovereenkomst): null = ongewijzigd.
          await authFetch(`${BACKEND_URL}/Api/permissions/users/${encodeURIComponent(user.email)}/gripp-velden`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              gripp_employee_id: grippMatch.id,
              contract_type: grippMatch.employmenttype?.searchname || grippMatch.employmenttype || null,
              contract_hours: grippMatch.hourspw || grippMatch.contracthours || null,
              hourly_rate: grippMatch.internalhourlyrate || grippMatch.hourlyrate || null,
              updated_by: userEmail,
            }),
          });
          updated++;
        }
      }

      toast.success(`Gripp data gesynchroniseerd (${updated}/${users.length} gebruikers bijgewerkt)`);
      await fetchData();
    } catch (err) {
      toast.error('Gripp sync mislukt: ' + err.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleEditOpen = (user) => {
    setEditUser({
      ...user,
      permission_overrides: user.permission_overrides || {},
    });
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    if (!editUser) return;
    try {
      // Only send overrides that differ from role defaults
      const defaults = roleConfig.defaults?.[editUser.role] || {};
      const overrides = {};
      let hasOverrides = false;
      for (const [feature, perms] of Object.entries(editUser.permission_overrides || {})) {
        const def = defaults[feature] || {};
        if (perms.view !== def.view || perms.edit !== def.edit) {
          overrides[feature] = perms;
          hasOverrides = true;
        }
      }

      const res = await authFetch(`${BACKEND_URL}/Api/permissions/users/${encodeURIComponent(editUser.email)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role: editUser.role,
          display_name: editUser.display_name,
          permission_overrides: hasOverrides ? overrides : null,
          is_active: editUser.is_active,
          // Uren/contract handmatig aanpasbaar (naast Gripp-sync). Leeg = ongewijzigd
          // (engine doet COALESCE, dus null overschrijft niet).
          contract_hours: (editUser.contract_hours === '' || editUser.contract_hours == null) ? null : Number(editUser.contract_hours),
          contract_type: (editUser.contract_type ?? '').trim() || null,
          updated_by: userEmail,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(`${editUser.display_name || editUser.email} bijgewerkt`);
      setEditOpen(false);
      await fetchData();
    } catch (err) {
      toast.error('Opslaan mislukt: ' + err.message);
    }
  };

  const getEffectivePermissions = (user) => {
    // Merge role defaults with user overrides
    const defaults = roleConfig.defaults?.[user?.role || 'team'] || {};
    const overrides = user?.permission_overrides || {};
    const merged = {};
    for (const [feature] of Object.entries(roleConfig.features || {})) {
      const def = defaults[feature] || { view: false, edit: false };
      const ovr = overrides[feature];
      merged[feature] = ovr ? { ...def, ...ovr } : { ...def };
    }
    return merged;
  };

  const toggleOverride = (feature, action) => {
    setEditUser(prev => {
      const defaults = roleConfig.defaults?.[prev.role] || {};
      const currentOverrides = { ...prev.permission_overrides };

      // Get current effective value
      const defVal = defaults[feature]?.[action] ?? false;
      const overrideVal = currentOverrides[feature]?.[action];
      const currentVal = overrideVal !== undefined ? overrideVal : defVal;

      // Toggle
      if (!currentOverrides[feature]) {
        currentOverrides[feature] = { ...defaults[feature] };
      }
      currentOverrides[feature][action] = !currentVal;

      return { ...prev, permission_overrides: currentOverrides };
    });
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  }

  const matchesQ = (u) => {
    const n = q.trim().toLowerCase();
    if (!n) return true;
    return (u.display_name || '').toLowerCase().includes(n) || (u.email || '').toLowerCase().includes(n);
  };
  const activeUsers = users.filter(u => u.is_active);
  const inactiveUsers = users.filter(u => !u.is_active);
  const shownActive = activeUsers.filter(matchesQ);
  const shownInactive = inactiveUsers.filter(matchesQ);

  return (
    <Box>
      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          startIcon={seeding ? <CircularProgress size={16} /> : <PersonAddIcon />}
          onClick={handleSeed}
          disabled={seeding}
          size="small"
        >
          Sync vanuit Airtable
        </Button>
        <Button
          variant="outlined"
          startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
          onClick={handleSyncGripp}
          disabled={syncing}
          size="small"
        >
          Gripp data ophalen
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
          {activeUsers.length} actieve gebruikers, {inactiveUsers.length} inactief
        </Typography>
        <TextField
          size="small"
          placeholder="Zoek gebruiker op naam of e-mail…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ ml: 'auto', minWidth: 260 }}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
        />
      </Box>

      {/* User Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 600, py: 1.5 } }}>
              <TableCell width={30}></TableCell>
              <TableCell>Naam</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Rol</TableCell>
              <TableCell>Functie</TableCell>
              <TableCell>Contract</TableCell>
              <TableCell align="center">Uren/week</TableCell>
              <TableCell align="center">Overrides</TableCell>
              <TableCell width={50}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shownActive.map(user => (
              <React.Fragment key={user.email}>
                <TableRow
                  hover
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                  onClick={() => setExpandedUser(expandedUser === user.email ? null : user.email)}
                >
                  <TableCell sx={{ py: 1 }}>
                    <IconButton size="small">
                      {expandedUser === user.email ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Typography variant="body2" fontWeight={500}>{user.display_name || '-'}</Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Chip
                      label={ROLE_LABELS[user.role] || user.role}
                      size="small"
                      sx={{
                        bgcolor: ROLE_COLORS[user.role] + '18',
                        color: ROLE_COLORS[user.role],
                        fontWeight: 600,
                        fontSize: '0.75rem',
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    {/* Functie uit de Airtable Personen-spiegel (via e-mail gematcht) */}
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 180 }}>
                      {user.functie || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {user.contract_type || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1 }}>
                    <Typography variant="body2">
                      {user.contract_hours != null ? `${user.contract_hours}u` : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1 }}>
                    {user.permission_overrides && Object.keys(user.permission_overrides).length > 0 ? (
                      <Chip label={Object.keys(user.permission_overrides).length} size="small" color="warning" variant="outlined" />
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleEditOpen(user); }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>

                {/* Expanded permissions row */}
                <TableRow>
                  <TableCell colSpan={9} sx={{ py: 0, border: expandedUser === user.email ? undefined : 'none' }}>
                    <Collapse in={expandedUser === user.email} timeout="auto" unmountOnExit>
                      <Box sx={{ py: 1.5, px: 1 }}>
                        <PermissionSummary
                          permissions={getEffectivePermissions(user)}
                          features={roleConfig.features || {}}
                          overrides={user.permission_overrides}
                          grippId={user.gripp_employee_id}
                          hourlyRate={user.hourly_rate}
                          categoryOrder={roleConfig.featureCategoryOrder}
                        />
                        {expandedUser === user.email && (
                          <Box sx={{ mt: 1.5 }}>
                            <Typography sx={{ fontFamily: '"Inter", system-ui, sans-serif', fontSize: '0.66rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#767c7e', mb: 1 }}>Standaard corvee-beschikbaarheid</Typography>
                            <CorveeMemberPanel email={user.email} readOnly showCredits />
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
            {shownActive.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  Geen gebruikers gevonden{q.trim() ? ` voor "${q.trim()}"` : ''}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Inactive users */}
      {shownInactive.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Inactieve gebruikers ({shownInactive.length})
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ opacity: 0.6 }}>
            <Table size="small">
              <TableBody>
                {shownInactive.map(user => (
                  <TableRow key={user.email}>
                    <TableCell>{user.display_name || '-'}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip label={ROLE_LABELS[user.role] || user.role} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleEditOpen(user)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Edit Dialog */}
      {editUser && (
        <EditUserDialog
          open={editOpen}
          user={editUser}
          setUser={setEditUser}
          roleConfig={roleConfig}
          onSave={handleEditSave}
          onClose={() => setEditOpen(false)}
          toggleOverride={toggleOverride}
          getEffective={getEffectivePermissions}
        />
      )}
    </Box>
  );
}


function PermissionSummary({ permissions, features, overrides, grippId, hourlyRate, categoryOrder }) {
  return (
    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
          Rechten
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {groupFeaturesByCategory(features, categoryOrder).map(([category, entries]) => (
            <Box key={category}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 0.25 }}>
                {category}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {entries.map(([key, feat]) => {
                  const perm = permissions[key];
                  const isOverridden = overrides && overrides[key];
                  return (
                    <Tooltip key={key} title={`${feat.label}: view=${perm?.view ? 'ja' : 'nee'}, edit=${perm?.edit ? 'ja' : 'nee'}${isOverridden ? ' (override)' : ''}`}>
                      <Chip
                        label={feat.label}
                        size="small"
                        variant={perm?.view ? 'filled' : 'outlined'}
                        color={perm?.view ? (perm?.edit ? 'success' : 'info') : 'default'}
                        sx={{
                          fontSize: '0.7rem',
                          height: 24,
                          border: isOverridden ? '2px solid #f59e0b' : undefined,
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      {(grippId || hourlyRate != null) && (
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
            Gripp
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {grippId ? `ID: ${grippId}` : ''}
            {grippId && hourlyRate != null ? ' | ' : ''}
            {hourlyRate != null ? `Tarief: \u20AC${hourlyRate}/u` : ''}
          </Typography>
        </Box>
      )}
    </Box>
  );
}


function EditUserDialog({ open, user, setUser, roleConfig, onSave, onClose, toggleOverride, getEffective }) {
  const features = roleConfig.features || {};
  const defaults = roleConfig.defaults?.[user.role] || {};
  const effective = getEffective(user);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {user.display_name || user.email} bewerken
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          {/* Role selector */}
          <FormControl size="small" fullWidth>
            <InputLabel>Rol</InputLabel>
            <Select
              value={user.role}
              label="Rol"
              onChange={(e) => setUser(prev => ({ ...prev, role: e.target.value }))}
            >
              {Object.entries(roleConfig.roles || {}).map(([key, r]) => (
                <MenuItem key={key} value={key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: ROLE_COLORS[key] }} />
                    {r.label}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {r.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Active toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={user.is_active}
                onChange={(e) => setUser(prev => ({ ...prev, is_active: e.target.checked }))}
              />
            }
            label="Actief"
          />

          {/* Contract & uren — handmatig aanpasbaar naast de Gripp-sync. Leeg laten =
              ongewijzigd. Handig voor mensen die niet (goed) uit Gripp matchen. */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              size="small" type="number" label="Uren per week" fullWidth
              value={user.contract_hours ?? ''}
              onChange={(e) => setUser(prev => ({ ...prev, contract_hours: e.target.value }))}
              inputProps={{ min: 0, max: 60, step: 1 }}
              helperText="Leeg = ongewijzigd"
            />
            <TextField
              size="small" label="Contracttype" fullWidth
              value={user.contract_type ?? ''}
              placeholder="bv. Vast, Freelance"
              onChange={(e) => setUser(prev => ({ ...prev, contract_type: e.target.value }))}
            />
          </Box>

          {/* Permission overrides — gegroepeerd per sectie/categorie */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Rechten (geel kader = override t.o.v. rolstandaard)
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, py: 0.75 }}>Recht / pagina</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, py: 0.75 }}>Bekijken</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, py: 0.75 }}>Bewerken</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupFeaturesByCategory(features, roleConfig.featureCategoryOrder).map(([category, entries]) => (
                  <React.Fragment key={category}>
                    <TableRow>
                      <TableCell colSpan={3} sx={{ py: 0.6, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary' }}>
                          {category}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {entries.map(([key, feat]) => {
                      const defView = defaults[key]?.view ?? false;
                      const defEdit = defaults[key]?.edit ?? false;
                      const effView = effective[key]?.view ?? false;
                      const effEdit = effective[key]?.edit ?? false;
                      const viewOverridden = user.permission_overrides?.[key]?.view !== undefined;
                      const editOverridden = user.permission_overrides?.[key]?.edit !== undefined;

                      return (
                        <TableRow key={key}>
                          <TableCell sx={{ py: 0.75 }}>
                            <Tooltip title={feat.description || ''} placement="top-start" arrow>
                              <Typography variant="body2">{feat.label}</Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.5 }}>
                            <Switch
                              size="small"
                              checked={effView}
                              onChange={() => toggleOverride(key, 'view')}
                              sx={viewOverridden && effView !== defView ? {
                                '& .MuiSwitch-track': { border: '2px solid #f59e0b' },
                              } : {}}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.5 }}>
                            <Switch
                              size="small"
                              checked={effEdit}
                              onChange={() => toggleOverride(key, 'edit')}
                              sx={editOverridden && effEdit !== defEdit ? {
                                '& .MuiSwitch-track': { border: '2px solid #f59e0b' },
                              } : {}}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </Box>

          {/* Gripp info (read-only) */}
          {(user.gripp_employee_id || user.contract_type || user.contract_hours != null) && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Gripp informatie
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                <TextField size="small" label="Gripp ID" value={user.gripp_employee_id || '-'} InputProps={{ readOnly: true }} />
                <TextField size="small" label="Contracttype" value={user.contract_type || '-'} InputProps={{ readOnly: true }} />
                <TextField size="small" label="Uren/week" value={user.contract_hours ?? '-'} InputProps={{ readOnly: true }} />
                <TextField size="small" label="Uurtarief" value={user.hourly_rate != null ? `\u20AC${user.hourly_rate}` : '-'} InputProps={{ readOnly: true }} />
              </Box>
            </Paper>
          )}

          {/* Standaard corvee-beschikbaarheid (slaat direct op) */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Standaard corvee-beschikbaarheid
            </Typography>
            <CorveeMemberPanel email={user.email} showCredits />
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuleren</Button>
        <Button variant="contained" onClick={onSave}>Opslaan</Button>
      </DialogActions>
    </Dialog>
  );
}
